// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Cultivation Section
// Path: src/components/donorDetail/CultivationSection.jsx
//
// Extracted from DonorDetailView.jsx during modularization Phase 3.
// Contains the full cultivation intelligence display: stat tiles,
// score bar, contact points, persona guidance, suggested move,
// planned next step, target date.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { ArrowRight, Target } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ═══════════════════════════════════════════════════════════════
// CultivationContent
// ═══════════════════════════════════════════════════════════════

export const CultivationContent = ({
  engagement,
  engPathway,
  scoreColor,
  daysColor,
  scorePct,
  selectedPersona,
  suggestion,
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  palette,
}) => (
  <>
    {/* ── Three stat tiles ──────────────────────────────────────── */}
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 16,
        marginBottom: 16,
      }}
    >
      {[
        {
          value: engagement.score,
          label: engPathway.label,
          color: scoreColor,
          sub: "engagement score",
        },
        {
          value:
            engagement.daysSinceContact === null
              ? "\u2014"
              : engagement.daysSinceContact,
          label:
            engagement.daysSinceContact === null
              ? "Never"
              : engagement.daysSinceContact <= 14
              ? "Recent"
              : engagement.daysSinceContact <= 30
              ? "Okay"
              : engagement.daysSinceContact <= 60
              ? "Stale"
              : "Overdue",
          color: daysColor,
          sub: "days since contact",
        },
        {
          value: engagement.activeContacts,
          label: `of ${engagement.totalContacts}`,
          color: null,
          sub: "active contacts",
        },
      ].map((tile, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            textAlign: "center",
            padding: "16px 12px",
            borderRadius: 10,
            background: "#ffffff",
            border: `1px solid ${palette.cardBorder}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              fontSize: "2.2rem",
              fontWeight: 800,
              color: palette.text1,
              lineHeight: 1,
              fontFamily: FONT_FAMILY,
            }}
          >
            {tile.value}
          </div>
          <div
            style={{
              display: "inline-block",
              fontSize: 11,
              fontWeight: 700,
              color: tile.color ? "#ffffff" : palette.text2,
              background: tile.color || "transparent",
              padding: "2px 10px",
              borderRadius: 999,
              marginTop: 6,
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            {tile.label}
          </div>
          <div style={{ fontSize: 10, color: palette.text3, marginTop: 4 }}>
            {tile.sub}
          </div>
        </div>
      ))}
    </div>

    {/* ── Score bar ─────────────────────────────────────────────── */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          flex: 1,
          height: 10,
          borderRadius: 5,
          background: "#f1f5f9",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${scorePct}%`,
            height: "100%",
            borderRadius: 5,
            background: scoreColor,
            transition: "width 0.4s ease",
          }}
        />
        {[10, 30, 80].map((t) => (
          <div
            key={t}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${(t / 150) * 100}%`,
              width: 1,
              background: "#cbd5e1",
            }}
          />
        ))}
      </div>
      <span
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          color: palette.text1,
          minWidth: 50,
          textAlign: "right",
        }}
      >
        {engagement.score} / 150
      </span>
    </div>

    {/* ── Status line ──────────────────────────────────────────── */}
    <div
      style={{
        fontSize: FONT_SIZES.sm,
        fontWeight: 600,
        padding: "10px 14px",
        borderRadius: 8,
        marginBottom: 16,
        textAlign: "center",
        lineHeight: 1.4,
        background: "#ffffff",
        border: `1px solid ${palette.cardBorder}`,
        color: palette.text1,
      }}
    >
      {engagement.daysSinceContact === null
        ? "\u26A0\uFE0F Never contacted \u2014 make the first move today"
        : engagement.daysSinceContact > 30
        ? "\u26A0\uFE0F This relationship is cooling \u2014 reconnect this week"
        : engagement.daysSinceContact > 14
        ? "Getting stale \u2014 schedule a check-in"
        : "\u2713 On track \u2014 maintain momentum"}
    </div>

    {/* ── Active contact points ─────────────────────────────────── */}
    {engagement.scoredContacts.length > 0 && (
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 6,
          }}
        >
          Active Contact Points
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {engagement.scoredContacts.slice(0, 8).map((sc, i) => (
            <span
              key={i}
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 4,
                background: "#ffffff",
                border: `1px solid ${palette.cardBorder}`,
                color: palette.text1,
                fontWeight: 500,
              }}
            >
              {sc.type} +{sc.decayedPoints}{" "}
              <span style={{ color: palette.text3, fontSize: 9 }}>
                ({Math.round(sc.decayFactor * 100)}%)
              </span>
            </span>
          ))}
          {engagement.scoredContacts.length > 8 && (
            <span
              style={{
                fontSize: 11,
                color: palette.text3,
                padding: "3px 4px",
              }}
            >
              +{engagement.scoredContacts.length - 8} more
            </span>
          )}
        </div>
      </div>
    )}

    {/* ── No contacts / all decayed ─────────────────────────────── */}
    {engagement.activeContacts === 0 && (
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 6,
          }}
        >
          {engagement.totalContacts > 0
            ? "All Contacts Decayed"
            : "No Contact History"}
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text2,
            lineHeight: 1.4,
          }}
        >
          {engagement.totalContacts > 0
            ? `${engagement.totalContacts} contact(s) on file but all older than 24 months. Re-engage to rebuild the score.`
            : "No contacts logged yet. Start tracking your engagement to build a score."}
        </div>
      </div>
    )}

    {/* ── Persona cultivation guidance ──────────────────────────── */}
    {selectedPersona && selectedPersona.actionHint && (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 14px",
          borderRadius: 8,
          background: `${selectedPersona.color || "#6b7280"}08`,
          border: `1px solid ${selectedPersona.color || "#6b7280"}25`,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: selectedPersona.color || "#6b7280",
            flexShrink: 0,
            marginTop: 4,
          }}
        />
        <div>
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              color: palette.text2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 2,
            }}
          >
            Cultivation Guidance \u2014 {selectedPersona.name}
          </div>
          <div
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text2,
              lineHeight: 1.5,
            }}
          >
            {selectedPersona.actionHint}
          </div>
        </div>
      </div>
    )}

    {/* ── Suggested next move ───────────────────────────────────── */}
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 8,
        background: "#ffffff",
        border: `1px solid ${palette.cardBorder}`,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          color: palette.text2,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 4,
        }}
      >
        <Target size={ICON_SIZES.xs} style={{ verticalAlign: "middle", marginRight: 4 }} />
        Suggested Next Move
      </div>
      <div style={{ fontSize: FONT_SIZES.sm, color: palette.text2 }}>
        <ArrowRight
          size={ICON_SIZES.xs}
          style={{ verticalAlign: "middle", marginRight: 4 }}
        />
        {suggestion}
      </div>
    </div>

    {/* ── Planned next step + target date ───────────────────────── */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          My Planned Next Step
        </span>
        <input
          type="text"
          value={detailForm.cultivationNextStep || ""}
          onChange={(e) => {
            if (canEdit) updateField("cultivationNextStep", e.target.value);
          }}
          onBlur={() => {
            if (canEdit)
              updateMember(
                selectedMember.id,
                { cultivationNextStep: detailForm.cultivationNextStep },
                true
              );
          }}
          readOnly={!canEdit}
          placeholder={
            canEdit
              ? "e.g., Invite to next event, Schedule lunch meeting..."
              : ""
          }
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`,
            background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
            color: palette.inputColor,
            outline: "none",
            opacity: canEdit ? 1 : 0.7,
          }}
          onFocus={(e) => {
            if (canEdit) {
              e.target.style.borderColor = "#10b981";
              e.target.style.boxShadow = "0 0 0 3px rgba(16,185,129,0.12)";
            }
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: 140,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Target Date
        </span>
        <input
          type="date"
          value={detailForm.cultivationTargetDate || ""}
          onChange={(e) => {
            if (canEdit) {
              updateField("cultivationTargetDate", e.target.value);
              updateMember(
                selectedMember.id,
                { cultivationTargetDate: e.target.value },
                true
              );
            }
          }}
          readOnly={!canEdit}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`,
            background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
            color: palette.inputColor,
            outline: "none",
            opacity: canEdit ? 1 : 0.7,
          }}
        />
      </div>
    </div>
  </>
);
